/**
 * Phone store — UI-only state for the docked softphone.
 *
 * Active call lifecycle lives in `lib/telephony/*` (the call registry + XState
 * machine, projected through `phone-state`). This store just holds UI concerns:
 *   - mirrored connection state (`status`) and mute flag (fed from phone-state)
 *   - device selections, user status
 *   - dial-pad open/closed
 *   - a snapshot of the call awaiting disposition, mirrored from phone-state so
 *     the form can be completed from any tab.
 */

import { create } from 'zustand';
import type { CallRecord, PhoneStatus } from '../lib/telephony';
import { updateDevicePreferences } from '../lib/telephony';

export type { CallerInfo, CallRecord } from '../lib/telephony';
export type ConnectionStatus = PhoneStatus;
export type UserStatus = 'available' | 'busy' | 'dnd';

const LAST_BRAND_KEY = 'office-app:phone-last-brand';

export function getLastBrand(): string | null {
  try {
    return localStorage.getItem(LAST_BRAND_KEY);
  } catch {
    return null;
  }
}

export function setLastBrand(brand: string) {
  try {
    localStorage.setItem(LAST_BRAND_KEY, brand);
  } catch {
    // ignore
  }
}

export interface DeviceRef {
  id: string | null;
  label: string | null;
}

interface PhoneState {
  status: ConnectionStatus;
  isMuted: boolean;
  microphone: DeviceRef;
  speaker: DeviceRef;
  /** Apart beltoon-apparaat; id null = volg de gesprek-speaker. */
  ringtoneSpeaker: DeviceRef;
  ringtoneVolume: number;
  userStatus: UserStatus;
  dtmfKeypadOpen: boolean;

  dialPadOpen: boolean;
  dialPadPrefill: { number?: string; brand?: string; customerId?: string; customerName?: string; sourceContext?: string; contextTaskId?: string; contextRef?: string; contextReden?: string } | null;

  /** Last completed call, awaiting disposition form submission. */
  dispositionCall: CallRecord | null;

  setStatus: (status: ConnectionStatus) => void;
  setMuted: (muted: boolean) => void;
  setUserStatus: (status: UserStatus) => void;
  setMicrophone: (ref: DeviceRef) => void;
  setSpeaker: (ref: DeviceRef) => void;
  setRingtoneSpeaker: (ref: DeviceRef) => void;
  setRingtoneVolume: (volume: number) => void;
  setDtmfKeypadOpen: (open: boolean) => void;
  /** Hydrate from Supabase preferences. No persist side-effect. */
  hydrateDevices: (prefs: {
    microphone: DeviceRef;
    speaker: DeviceRef;
    ringtoneSpeaker: DeviceRef;
    ringtoneVolume: number;
  }) => void;

  setDialPadOpen: (open: boolean, prefill?: PhoneState['dialPadPrefill']) => void;

  setDispositionCall: (call: CallRecord | null) => void;
}

export const usePhoneStore = create<PhoneState>()((set) => ({
  status: 'disconnected',
  isMuted: false,
  microphone: { id: null, label: null },
  speaker: { id: null, label: null },
  ringtoneSpeaker: { id: null, label: null },
  ringtoneVolume: 0.3,
  userStatus: 'available',
  dtmfKeypadOpen: false,

  dialPadOpen: false,
  dialPadPrefill: null,

  dispositionCall: null,

  setStatus: (status) => set({ status }),
  setMuted: (isMuted) => set({ isMuted }),
  setUserStatus: (userStatus) => set({ userStatus }),
  setMicrophone: (microphone) => {
    set({ microphone });
    updateDevicePreferences({
      microphoneId: microphone.id,
      microphoneLabel: microphone.label,
    });
  },
  setSpeaker: (speaker) => {
    set({ speaker });
    updateDevicePreferences({
      speakerId: speaker.id,
      speakerLabel: speaker.label,
    });
  },
  setRingtoneSpeaker: (ringtoneSpeaker) => {
    set({ ringtoneSpeaker });
    updateDevicePreferences({
      ringtoneSpeakerId: ringtoneSpeaker.id,
      ringtoneSpeakerLabel: ringtoneSpeaker.label,
    });
  },
  setRingtoneVolume: (ringtoneVolume) => {
    const clamped = Math.max(0, Math.min(1, ringtoneVolume));
    set({ ringtoneVolume: clamped });
    updateDevicePreferences({ ringtoneVolume: clamped });
  },
  setDtmfKeypadOpen: (dtmfKeypadOpen) => set({ dtmfKeypadOpen }),
  hydrateDevices: ({ microphone, speaker, ringtoneSpeaker, ringtoneVolume }) =>
    set({ microphone, speaker, ringtoneSpeaker, ringtoneVolume }),

  setDialPadOpen: (open, prefill) => set({
    dialPadOpen: open,
    dialPadPrefill: open ? (prefill ?? null) : null,
  }),

  setDispositionCall: (call) => set({ dispositionCall: call }),
}));
